import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { filter, Subscription } from 'rxjs';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { UserModel } from '../../../../Core/Users/models/user.model';

import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ResourceActionTypes } from '../../../../Common/Basic/enums/resource-actions.enum';

import { AccountNoteModel } from '../../models/account-note.model';

export interface AccountNoteChange {
    type: ResourceActionTypes;
    note: AccountNoteModel;
}

@Component({
    selector: 'agpr-account-notes',
    templateUrl: './account-notes.component.html',
    styleUrls: ['./account-notes.component.scss'],
    standalone: false
})
export class AccountNotesComponent implements OnInit, OnDestroy {
    public readonly buttonTypes = ButtonTypesEnum;
    public readonly messageSeverity = messageSeverityEnum;

    @ViewChild('actionButtons') actionButtons: TemplateRef<any>;

    @Input() id: string;
    @Input() set accountNotes(notes: AccountNoteModel[]) {
        if (!notes) {
            return;
        }
        this._accountNotes = notes.sort(
            (a, b) => b.lastUpdatedDate?.getTime() - a.lastUpdatedDate?.getTime()
        ).slice();
    }
    @Input() maxLength: number = 2000;
    @Input() selfModificationOnly: boolean = false;

    @Output() noteChanged: EventEmitter<AccountNoteChange> = new EventEmitter<AccountNoteChange>(null);

    subscriptions: Subscription[] = [];

    usersMap: {[key: number]: string} = {};
    currentUser: UserModel;
    timezone: string;
    dateFormat: string;

    _accountNotes: AccountNoteModel[] = [];
    noteText: string;
    showAddNoteEditor: boolean = false;
    currentTime: Date;
    editingNote: AccountNoteModel;

    constructor(
        public currentUserStateService: CurrentUserStateService,
        public coreDialogService: CoreDialogService
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserStateService.getUser().subscribe(user => {
                this.currentUser = user;
            }),
            this.currentUserStateService.getAccountUsers().subscribe(users => {
                this.usersMap = {};

                if (users) {
                    users.forEach(user => {
                        this.usersMap[user.id] = user.getDisplayName();
                    });
                }
            }),
            this.currentUserStateService.getPreferences().pipe(
                filter(x => !!x)
            ).subscribe(preferences => {
                this.timezone = preferences.timeZone;
                this.dateFormat = preferences.getDateFormatString(true, true, true);
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.map(subscription => subscription.unsubscribe());
    }

    openAddNote() {
        this.noteText = null;
        this.currentTime = new Date();
        this.showAddNoteEditor = true;
    }

    addNote() {
        const noteText = this.noteText?.trim();
        if (noteText?.length) {
            const note = new AccountNoteModel();
            note.value = noteText;
            this.noteChanged.emit({ type: ResourceActionTypes.ADD_NOTE, note } );
        }
        this.showAddNoteEditor = false;
        this.noteText = null;
    }

    openEditNote(note: AccountNoteModel) {
        this.editingNote = note;
        this.noteText = note.value;
        this.currentTime = new Date();
    }

    editNote() {
        const noteText = this.noteText?.trim();
        if (noteText?.length && noteText !== this.editingNote?.value?.trim()) {
            this.editingNote.value = noteText;
            this.noteChanged.emit({ type: ResourceActionTypes.EDIT_NOTE, note: this.editingNote });
        }
        this.editingNote = null;
        this.noteText = null;
    }

    openDeleteNote(note: AccountNoteModel) {
        this.coreDialogService.open(
            ConfirmDialogComponent,
            {
                inputs: {
                    heading: 'Discover.userNotes.deleteNote',
                    message: 'AMT.RHP.notes.delete.message',
                    okLabel: 'AMT.RHP.notes.delete.okLabel',
                    cancelLabel: 'AMT.RHP.notes.delete.cancelLabel',
                    width: 350
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.noteChanged.emit({ type: ResourceActionTypes.DELETE_NOTE, note } );
                        }
                    }
                }
            }
        );
    }
}
